import {
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

interface PhoneCallCardProps {
  call: UnifiedCommunication;
}

export const PhoneCallCard: React.FC<PhoneCallCardProps> = ({ call }) => {
  const { selectCommunication, selectedCommunication, isRead } =
    useClientCommunications();

  const isInbound = call.direction === 'inbound';
  const isMissed = !call.duration_seconds || call.duration_seconds < 10;
  const read = isRead(call.id);
  const isSelected = selectedCommunication?.id === call.id;

  const handleSelect = () => {
    selectCommunication(call);
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Determine icon and colors based on call type
  const getCallIcon = () => {
    if (isMissed && isInbound) {
      return {
        Icon: PhoneMissed,
        bgColor: 'bg-red-50',
        iconColor: 'text-red-500',
      };
    }
    if (isInbound) {
      return {
        Icon: PhoneIncoming,
        bgColor: 'bg-primary/10',
        iconColor: 'text-primary',
      };
    }
    return {
      Icon: PhoneOutgoing,
      bgColor: 'bg-primary/10',
      iconColor: 'text-primary',
    };
  };

  const { Icon, bgColor, iconColor } = getCallIcon();

  const durationLabel = isMissed
    ? isInbound
      ? 'Missed'
      : 'No answer'
    : formatDuration(call.duration_seconds);

  // Get call title
  const getCallTitle = () => {
    if (call.preview && call.preview.length > 10) {
      const firstSentence = call.preview.split(/[.!?]/)[0];
      if (firstSentence.length > 50) {
        return `${firstSentence.substring(0, 47)}...`;
      }
      return firstSentence;
    }

    if (isMissed && isInbound) {
      return 'Missed call';
    }
    if (isInbound) {
      return 'Incoming call';
    }
    return 'Outgoing call';
  };

  return (
    <div
      className={cn(
        'group flex items-center gap-3 p-3 rounded-xl border bg-white',
        'hover:border-zinc-200 hover:bg-zinc-50/50 transition-all cursor-pointer',
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-zinc-100',
        !read && isInbound && !isSelected && 'border-l-2 border-l-primary',
      )}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleSelect();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center',
          bgColor,
        )}
      >
        <Icon className={cn('h-4 w-4', iconColor)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm font-semibold text-zinc-900 truncate',
              !read && isInbound && 'text-zinc-950',
            )}
          >
            {getCallTitle()}
          </span>
          {durationLabel && (
            <span className="text-xs text-zinc-500 flex-shrink-0">
              • {durationLabel}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 truncate mt-0.5">
          {formatTime(call.timestamp)}
        </p>
      </div>

      {/* View details text */}
      <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        View details &rarr;
      </span>
    </div>
  );
};

export default PhoneCallCard;
